import { urlSchema } from '@form-factory/schema/contracts';
import { DEFAULT_DOCUMENT_WEIGHT } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, Input, Label, toast } from '@utima/ui';
import { Globe } from 'lucide-react';
import { memo, useCallback, useState, useEffect } from 'react';
import { v7 as uuidV7 } from 'uuid';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { Alert } from '@/components/Alert';
import { useUser } from '@/hooks';
import { trpc } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

import { WebsiteRecords } from './websiteRecords/WebsiteRecords';

export type WebsiteFormProps = {
  chatIds: string[];
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  projectedLimitReached: boolean;
  onPendingCountChange: (count: number) => void;
};

export const AddWebsites = memo(function WebsiteForm({
  chatIds,
  setOpen,
  defaultUrl,
  limitReached,
  projectedLimitReached,
  onPendingCountChange,
}: WebsiteFormProps) {
  const { t } = useLingui();
  const [isCrawling, setIsCrawling] = useState(false);
  const [isUriValid, setIsUriValid] = useState(true);
  const [search, setSearch] = useState('');
  const utils = trpc.useUtils();
  const { uri, setUri, webUrls, setWebUrls } = useBoundStore(
    useShallow(state => ({
      uri: state.uri,
      setUri: state.setUri,
      webUrls: state.webUrls,
      setWebUrls: state.setWebUrls,
      user: state.user,
    })),
  );

  const {
    mutateAsync: mapUrl,
    isPending,
    reset: resetMapUrl,
  } = trpc.documents.mapUrl.useMutation();

  const { mutateAsync: createWebsite, isPending: isCreatingWebsite } =
    trpc.documents.createWebsite.useMutation({
      onSuccess: async () => {
        await Promise.all([
          utils.documents.findAll.invalidate(),
          utils.chats.findAll.invalidate(),
        ]);
      },
    });

  const selectedUrls = webUrls.filter(url => url.selected);

  const { organization, currentOrganizationId } = useUser();
  const organizationUrl = organization?.url ?? '';

  /**
   * Validates the uri.
   */
  const validateUri = useCallback(() => {
    const result = urlSchema.safeParse(uri);

    setIsUriValid(result.success);
    if (result.success) {
      setUri(result.data); // already normalized
    }

    return result.success;
  }, [setUri, uri]);

  /**
   * Changes local search state and updates the store.
   */
  const handleUriChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUri(e.target.value);
      setIsUriValid(true);
    },
    [setUri],
  );

  /**
   * Handle the crawl of a website.
   */
  const handleCrawl = useCallback(async () => {
    if (!validateUri()) {
      return;
    }

    if (!uri) {
      return;
    }

    setIsCrawling(true);

    // Reset the state
    setWebUrls([]);
    setSearch('');

    const response = await mapUrl({ url: uri });

    // Store the new URLs in the global state
    setWebUrls(
      response?.map(url => ({
        id: uuidV7(),
        url,
        selected: true,
        weight: DEFAULT_DOCUMENT_WEIGHT,
      })) ?? [],
    );

    setIsCrawling(false);
  }, [mapUrl, setWebUrls, uri, validateUri]);

  /**
   * Reset all urls and search queries.
   */
  const handleReset = useCallback(() => {
    setUri('');
    setSearch('');
    setWebUrls([]);
  }, [setUri, setSearch, setWebUrls]);

  /**
   * Handle the submission of the selected urls.
   */
  const handleAdd = useCallback(async () => {
    if (projectedLimitReached && !limitReached) {
      toast.error(
        t`Cannot add more documents. Please remove some selected documents.`,
      );

      return;
    }

    if (limitReached) {
      toast.error(
        t`Cannot add more documents. Please remove some existing documents.`,
      );

      return;
    }

    // Basic URI validation first
    if (uri && !selectedUrls.length && !validateUri()) {
      return;
    }

    const orgId = currentOrganizationId;
    if (!orgId) {
      toast.error(t`Cannot determine organization ID.`);

      return;
    }

    // --- Proceed with submission logic ---
    let finalUrls: string[] = [];
    let weights: Record<string, number> = {};

    if (uri && !selectedUrls.length) {
      finalUrls = [uri];
      weights = { [uri]: DEFAULT_DOCUMENT_WEIGHT };
    } else {
      finalUrls = Array.from(new Set(selectedUrls.map(url => url.url)));
      weights = selectedUrls.reduce(
        (acc, url) => {
          acc[url.url] =
            typeof url.weight === 'number'
              ? url.weight
              : DEFAULT_DOCUMENT_WEIGHT;

          return acc;
        },
        {} as Record<string, number>,
      );
    }

    // Now call the mutation (validation passed)
    await createWebsite({
      urls: finalUrls,
      chats: chatIds,
      weights,
    });

    // Replace current URL to remove the 'open' query parameter
    const url = new URL(window.location.href);
    url.searchParams.delete('open');
    window.history.replaceState(null, '', url.toString());

    resetMapUrl();
    handleReset();
    setOpen(false);
  }, [
    projectedLimitReached,
    limitReached,
    uri,
    selectedUrls,
    validateUri,
    createWebsite,
    chatIds,
    resetMapUrl,
    handleReset,
    setOpen,
    t,
    currentOrganizationId,
  ]);

  // Effect to update the pending count for the parent modal
  useEffect(() => {
    let count = 0;
    const isValidUri = z.string().url().safeParse(uri).success;

    if (webUrls.length > 0) {
      // If crawled URLs exist, count selected ones
      count = webUrls.filter(url => url.selected).length;
    } else if (uri && isValidUri) {
      // If no crawled URLs, but a valid URI exists, count is 1
      count = 1;
    }
    // Otherwise, count is 0 (invalid URI, no crawled URLs/selection)

    onPendingCountChange(count);
  }, [uri, webUrls, onPendingCountChange]); // Depend on uri and the webUrls array (including selection state)

  return (
    <>
      {/* Show Alert if limit reached */}
      {limitReached && (
        <Alert
          variant='warning'
          title={t`Document Limit Reached`}
          className='mb-4'
        >
          <Trans>
            You have reached the maximum document limit for your plan.
          </Trans>
        </Alert>
      )}
      <div>
        <div className='mb-3 flex flex-col gap-2'>
          <div className='flex w-full items-end gap-2'>
            <div className='flex w-full flex-col gap-2'>
              <Label htmlFor='uri'>{t`URL`}:</Label>
              <Input
                name='uri'
                data-cta='admin-add-websites-uri-input'
                placeholder='https://www.yourwebsite.com'
                onChange={handleUriChange}
                value={uri ?? defaultUrl ?? organizationUrl}
                required
              />
            </div>

            <Button
              data-cta='admin-add-websites-map-button'
              size='sm'
              disabled={isPending || isCrawling || !uri}
              className='whitespace-nowrap'
              loading={isPending || isCrawling}
              onClick={handleCrawl}
              icon={<Globe className='size-4' />}
            >
              <Trans>Crawl website</Trans>
            </Button>
          </div>

          {!isUriValid && (
            <span className='text-sm text-red-500'>
              {t`URL is not valid, it should start with https://`}
            </span>
          )}
        </div>

        <WebsiteRecords search={search} setSearch={setSearch} />
      </div>

      <Dialog.Footer className='z-50 flex flex-row justify-end gap-2 md:flex-row'>
        {webUrls.length > 0 && (
          <Button
            data-cta='admin-add-websites-reset-button'
            variant='secondary'
            size='sm'
            onClick={() => handleReset()}
          >
            <Trans>Reset</Trans>
          </Button>
        )}
        <Button
          data-cta='admin-add-websites-add-button'
          disabled={
            isCreatingWebsite ||
            limitReached ||
            projectedLimitReached ||
            (!selectedUrls.length && !uri)
          }
          loading={isCreatingWebsite}
          onClick={handleAdd}
        >
          <Trans>Add</Trans>
        </Button>
      </Dialog.Footer>
    </>
  );
});
